import type {ReactNode, ComponentPropsWithoutRef} from 'react';
import React, {createElement, forwardRef} from 'react';

import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {COLOR_VARIANT_CLASSES, VARIANT_CLASSES} from './constants';
import {ButtonIcon} from './button-icon.component';

import styles from './button.component.module.styl';

const classNames = withCssModule(styles);
const classes = hashedClasses(styles);
const noop = () => undefined;

type NativeButtonProps = ComponentPropsWithoutRef<'button'>;

export interface ButtonProps extends NativeButtonProps {
    /**
     * Element type
     */
    as?: 'button' | 'a',

    /**
     * Set component to busy state
     */
    busy?: boolean,

    /**
     * Circled button type
     */
    circled?: boolean,

    /**
     * Set component to current state
     */
    current?: boolean,

    /**
     * Specifies the URL the hyperlink points to. Use with as='a'
     * Temporary solution until Flow is removed
     */
    href?: ComponentPropsWithoutRef<'a'>['href'],

    /**
     * Link target. Use with as='a'
     * Temporary solution until Flow is removed
     */
    target?: ComponentPropsWithoutRef<'a'>['target'],

    /**
     * Style variants
     */
    variant?: 'Type.PRIMARY' | 'Type.SECONDARY' | 'Type.TERTIARY' | 'Type.PAGINATION' | 'Type.BACK',

    /**
     * Color variant of button
     */
    colorVariant?: 'ERROR' | 'ACCENT' | 'DEFAULT',

    /**
     * Button size
     */
    size?: 'small' | 'normal',

    /**
     * Apply dark mode styles
     * @deprecated It will be removed in 6 months 03-03-2026. Use default appearance
     */
    darkMode?: boolean,

    /**
     * Ref for button
     * */
    ref?: React.Ref<HTMLButtonElement>,

    /**
     * Icon before caption
     */
    startIcon?: ReactNode,

    /**
     * Icon after caption
     */
    endIcon?: ReactNode,
}


/**
 * ## Accessibility
 * When Button is busy - add the appropriate aria-label for Button.
 * This component implements the WAI-ARIA authoring practices for a button role.
 * For more information, see [WAI-ARIA Authoring Practices](https://www.w3.org/WAI/ARIA/apg/patterns/button/).
 *
 * @description To display a loader in the Flat Button during the busy state, pass the loader icon
 * via the startIcon prop.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/components-button--default-button}
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
    function Button(props, ref) {
        const {
            as = 'button',
            className,
            darkMode,
            current,
            circled,
            size = 'normal',
            busy,
            startIcon,
            endIcon,
            type = as === 'button' ? 'button' : undefined,
            variant = 'Type.PRIMARY',
            colorVariant = 'DEFAULT',
            disabled,
            ...restProps
        } = props;

        const elementClassName = classNames(className, 'button',
            VARIANT_CLASSES[variant],
            COLOR_VARIANT_CLASSES[colorVariant],
            {
                'button_small': size === 'small',
                'button_busy': busy,
                'button_current': current,
                'button_circled': circled,
                'button_dark-mode': darkMode && !(variant === 'Type.PAGINATION' || variant === 'Type.BACK'),
            },
        );

        return createElement(
            as,
            {
                'aria-disabled': busy || disabled,
                ref,
                disabled,
                ...restProps,
                type,
                'onClick': busy ? noop : restProps.onClick,
                ...(busy ? {'aria-label': restProps['aria-label'] || 'Loading'} : {}),
                'className': elementClassName,
            },
            (
                <div className={classes.button__hole}>
                    <ButtonIcon>
                        {startIcon}
                    </ButtonIcon>
                    <span className={classes.button__caption}>{props.children}</span>
                    <ButtonIcon right={true}>
                        {endIcon}
                    </ButtonIcon>
                </div>
            ),
            busy ? (
                <div className={classes.button__busy} aria-hidden="true">
                    <span className={classes.button__busy_1}/>
                    <span className={classes.button__busy_2}/>
                    <span className={classes.button__busy_3}/>
                </div>
            ) : null,
        );
    });

Button.displayName = 'Button';
